// ============================================================================
// 텍스처 로더 — 로딩 화면 없음. 즉시 폴백(캔버스 단색) 으로 시작하고 이미지가 오면 교체.
// ?asset=0 이면 폴백만 사용.
// ============================================================================
import * as THREE from 'three';
import { CARD_KINDS, CardKind } from './params';
import { CARD_ASPECT } from './cards.ts';
import { binderMeta, BinderMeta, CellUV, SHEET_PX, COVER_PX } from './binder-layout';

export interface TexSet {
  packFront: THREE.Texture;
  packInner: THREE.Texture;
  cardBack: THREE.Texture;
  // 등급당 1장인 **앞면 폴백**이다. 개봉 화면의 정본은 카드별 그림(cards/<id>.webp)이고,
  // 이 4장은 그 카드가 아직 안 왔거나 / 파일이 없거나 / 그 등급에 레지스트리 카드가 0장일 때
  // 쓰인다 (Renderer.applyCardTextures). 키는 애셋 파일 이름이기도 하다.
  cardFronts: Record<CardKind, THREE.Texture>;
  // 바인더 (BINDER_DESIGN §5.2). 애셋이 없으면 폴백 캔버스가 그대로 남는다.
  binderCover: THREE.Texture;
  binderSheet: THREE.Texture;
  binderSheetBack: THREE.Texture;
  binderGloss: THREE.Texture;
  binderGlossBack: THREE.Texture;
}

export const BINDER_TEX_NAMES = ['binder_cover', 'binder_sheet', 'binder_sheet_back', 'binder_gloss', 'binder_gloss_back'] as const;

function prep(t: THREE.Texture): THREE.Texture {
  t.colorSpace = THREE.SRGBColorSpace;
  t.premultiplyAlpha = false;
  t.generateMipmaps = true;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.anisotropy = 4;
  t.needsUpdate = true;
  return t;
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function canvasTex(w: number, h: number, draw: (ctx: CanvasRenderingContext2D) => void): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, w, h);
  draw(ctx);
  const t = new THREE.CanvasTexture(c);
  return prep(t);
}

export function fallbackPackFront(perfFrac: number): THREE.Texture {
  return canvasTex(512, 768, (ctx) => {
    const g = ctx.createLinearGradient(0, 0, 512, 768);
    g.addColorStop(0, '#2c5ad8');
    g.addColorStop(1, '#6a2bb5');
    ctx.fillStyle = g;
    // 톱니 측면
    ctx.beginPath();
    ctx.moveTo(14, 8);
    for (let y = 8; y < 760; y += 12) {
      ctx.lineTo(14 + (((y / 12) | 0) % 2 ? 6 : 0), y);
    }
    ctx.lineTo(14, 760);
    ctx.lineTo(498, 760);
    for (let y = 760; y > 8; y -= 12) {
      ctx.lineTo(498 - (((y / 12) | 0) % 2 ? 6 : 0), y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#f2ecd8';
    ctx.beginPath();
    ctx.ellipse(256, 230, 120, 62, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.setLineDash([12, 8]);
    const py = perfFrac * 768;
    ctx.beginPath();
    ctx.moveTo(20, py);
    ctx.lineTo(492, py);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(255,255,255,0.18)';
    for (let i = 0; i < 5; i++) {
      ctx.beginPath();
      ctx.moveTo(40 + i * 90, 740);
      ctx.lineTo(160 + i * 90, 330);
      ctx.lineTo(190 + i * 90, 330);
      ctx.lineTo(70 + i * 90, 740);
      ctx.fill();
    }
  });
}

export function fallbackPackInner(): THREE.Texture {
  return canvasTex(512, 768, (ctx) => {
    ctx.fillStyle = '#9a9891';
    ctx.fillRect(10, 6, 492, 756);
    ctx.strokeStyle = 'rgba(255,255,255,0.18)';
    ctx.lineWidth = 1;
    let s = 7;
    const r = () => ((s = (s * 16807) % 2147483647) / 2147483647);
    for (let i = 0; i < 400; i++) {
      ctx.beginPath();
      const x = 10 + r() * 492, y = 6 + r() * 756;
      ctx.moveTo(x, y);
      ctx.lineTo(x + (r() - 0.5) * 60, y + (r() - 0.5) * 60);
      ctx.stroke();
    }
    ctx.strokeStyle = 'rgba(0,0,0,0.12)';
    for (let i = 0; i < 400; i++) {
      ctx.beginPath();
      const x = 10 + r() * 492, y = 6 + r() * 756;
      ctx.moveTo(x, y);
      ctx.lineTo(x + (r() - 0.5) * 60, y + (r() - 0.5) * 60);
      ctx.stroke();
    }
  });
}

// 폴백 카드 캔버스. 그림 규격(1024×1432)과 **같은 종횡비**여야 메시에 눌려 보이지 않는다.
// 아래 그리기 좌표는 옛 512×768 공간 그대로 두고 세로만 눌러서 쓴다 (도형이 몇 % 납작해질 뿐이다).
const FB_CARD_W = 512;
const FB_CARD_H = Math.round(FB_CARD_W * CARD_ASPECT);

function fallbackCard(kind: string): THREE.Texture {
  return canvasTex(FB_CARD_W, FB_CARD_H, (ctx) => {
    ctx.scale(1, FB_CARD_H / 768);
    roundedRect(ctx, 10, 12, 492, 744, 26);
    ctx.fillStyle = kind === 'card_ur' ? '#c9a13a' : '#efe6cf';
    ctx.fill();
    if (kind === 'card_back') {
      roundedRect(ctx, 34, 36, 444, 696, 18);
      ctx.fillStyle = '#1b2d6b';
      ctx.fill();
      ctx.strokeStyle = '#efe6cf';
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.arc(256, 384, 120, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(256, 384, 46, 0, Math.PI * 2);
      ctx.stroke();
      return;
    }
    const win = { x: 40, y: 44, w: 432, h: kind === 'card_ar' || kind === 'card_ur' ? 680 : 440 };
    ctx.fillStyle = { card_common: '#7fc46b', card_rare: '#6aa9e6', card_ar: '#d88ad1', card_ur: '#3b2a6b' }[kind] ?? '#888';
    ctx.fillRect(win.x, win.y, win.w, win.h);
    // 단순 생물 실루엣
    ctx.fillStyle = { card_common: '#e59a3c', card_rare: '#2f4f9e', card_ar: '#f7f2ff', card_ur: '#e0c060' }[kind] ?? '#444';
    ctx.beginPath();
    ctx.ellipse(256, win.y + win.h * 0.55, 110, 90, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(256, win.y + win.h * 0.32, 70, 0, Math.PI * 2);
    ctx.fill();
    if (win.h < 600) {
      ctx.fillStyle = '#d9d9d9';
      ctx.fillRect(40, 510, 432, 220);
      ctx.fillStyle = '#b0b0b0';
      for (let i = 0; i < 5; i++) ctx.fillRect(60, 535 + i * 38, 392 - (i % 2) * 90, 10);
    }
  });
}

// ---------------------------------------------------------------- 바인더 폴백
// 좌표는 전부 binder-layout 의 FALLBACK_BINDER_META 에서 온다 — 그림과 좌표가 어긋나면
// 카드가 슬리브 밖에 걸린다. 측정값이 도착하면 그림도 좌표도 같이 갈아끼운다 (§5.8).

/** 펼친 바인더 커버 속면 (가로). 등이 정확히 가운데, 무광 은색 링 3개. */
export function fallbackBinderCover(): THREE.Texture {
  return canvasTex(COVER_PX.w, COVER_PX.h, (ctx) => {
    const m = binderMeta().cover;
    const r = m.rect;
    ctx.fillStyle = '#1d2a4a';
    roundedRect(ctx, r.x, r.y, r.w, r.h, 40);
    ctx.fill();
    // 가죽 결
    ctx.save();
    roundedRect(ctx, r.x, r.y, r.w, r.h, 40);
    ctx.clip();
    let sd = 91;
    const rnd = () => ((sd = (sd * 16807) % 2147483647) / 2147483647);
    ctx.strokeStyle = 'rgba(255,255,255,0.035)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 1400; i++) {
      const x = r.x + rnd() * r.w, y = r.y + rnd() * r.h;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + (rnd() - 0.5) * 26, y + (rnd() - 0.5) * 26);
      ctx.stroke();
    }
    ctx.restore();
    // 등 띠
    const sx = m.spineX;
    ctx.fillStyle = '#151f38';
    ctx.fillRect(sx - 34, r.y, 68, r.h);
    // 링 3개 (무광 은색 — 이 그림에서 유일한 은색)
    ctx.fillStyle = '#a9aeb4';
    for (let i = 0; i < 3; i++) {
      const cy = r.y + r.h * (0.25 + i * 0.25);
      ctx.beginPath();
      ctx.arc(sx, cy, 30, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(sx, cy, 17, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
    }
  });
}

function drawSheet(ctx: CanvasRenderingContext2D, meta: BinderMeta, mirror: boolean): void {
  if (mirror) {
    ctx.translate(SHEET_PX.w, 0);
    ctx.scale(-1, 1);
  }
  const r = meta.sheet.rect;
  ctx.fillStyle = '#cfcbc2';
  roundedRect(ctx, r.x, r.y, r.w, r.h, 18);
  ctx.fill();
  // 링 구멍 띠 (앞면은 왼쪽). 뒷면은 mirror 로 오른쪽에 온다.
  const stripW = meta.sheet.ringStripW * r.w;
  ctx.fillStyle = '#c4c0b6';
  ctx.fillRect(r.x, r.y, stripW, r.h);
  ctx.globalCompositeOperation = 'destination-out';
  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    ctx.arc(r.x + stripW / 2, r.y + r.h * (0.25 + i * 0.25), stripW * 0.32, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalCompositeOperation = 'source-over';
  // 9칸 — 안쪽은 단일 평탄색 (측정 도구가 flood fill 로 재는 그 색)
  for (const c of meta.sheet.cellsUV) {
    const x = r.x + c.u0 * r.w, y = r.y + c.v0 * r.h, w = (c.u1 - c.u0) * r.w, h = (c.v1 - c.v0) * r.h;
    roundedRect(ctx, x, y, w, h, meta.sheet.cellRadius);
    ctx.fillStyle = '#a8adb5';
    ctx.fill();
    ctx.strokeStyle = '#8d9198';
    ctx.lineWidth = 3;
    ctx.stroke();
  }
}

export function fallbackBinderSheet(back = false): THREE.Texture {
  return canvasTex(SHEET_PX.w, SHEET_PX.h, (ctx) => drawSheet(ctx, binderMeta(), back));
}

/** 절차 슬리브 앞판 — 카드 **위**에 덮이는 필름과 테두리. 광택 줄무늬는 굽지 않는다 (코드가 얹는다). */
function drawGloss(ctx: CanvasRenderingContext2D, meta: BinderMeta, mirror: boolean): void {
  if (mirror) {
    ctx.translate(SHEET_PX.w, 0);
    ctx.scale(-1, 1);
  }
  const r = meta.sheet.rect;
  const cells: CellUV[] = meta.sheet.cellsUV;
  for (const c of cells) {
    const x = r.x + c.u0 * r.w, y = r.y + c.v0 * r.h, w = (c.u1 - c.u0) * r.w, h = (c.v1 - c.v0) * r.h;
    roundedRect(ctx, x, y, w, h, meta.sheet.cellRadius);
    ctx.fillStyle = 'rgba(255,255,255,0.08)';
    ctx.fill();
    roundedRect(ctx, x + 2, y + 2, w - 4, h - 4, meta.sheet.cellRadius);
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 3;
    ctx.stroke();
  }
  // 칸 사이 용접선
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.lineWidth = 2;
  for (let i = 0; i < 2; i++) {
    const a = cells[i], b = cells[i + 1];
    const mx = r.x + ((a.u1 + b.u0) / 2) * r.w;
    ctx.beginPath();
    ctx.moveTo(mx, r.y);
    ctx.lineTo(mx, r.y + r.h);
    ctx.stroke();
  }
  for (let i = 0; i < 2; i++) {
    const a = cells[i * 3], b = cells[(i + 1) * 3];
    const my = r.y + ((a.v1 + b.v0) / 2) * r.h;
    ctx.beginPath();
    ctx.moveTo(r.x, my);
    ctx.lineTo(r.x + r.w, my);
    ctx.stroke();
  }
}

export function fallbackBinderGloss(back = false): THREE.Texture {
  return canvasTex(SHEET_PX.w, SHEET_PX.h, (ctx) => drawGloss(ctx, binderMeta(), back));
}

const RANK_COLOR = ['#7fc46b', '#6aa9e6', '#d88ad1', '#c9a13a'];

/**
 * 카드 그림이 아직(또는 영영) 없을 때 칸에 그리는 카드. 그림은 없어도
 * "있다 / 없다 / 몇 장" 은 보여야 한다. 카드 한 장 크기의 텍스처라 UV 는 0..1 이다.
 */
export function fallbackFaceCard(rank: number, label: string): THREE.Texture {
  return canvasTex(290, 405, (ctx) => {
    roundedRect(ctx, 8, 8, 274, 389, 16);
    ctx.fillStyle = RANK_COLOR[rank] ?? '#888';
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    ctx.lineWidth = 5;
    ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.fillRect(26, 30, 238, 210);
    ctx.fillStyle = 'rgba(0,0,0,0.75)';
    ctx.font = 'bold 30px ui-monospace, monospace';
    ctx.textAlign = 'center';
    ctx.fillText(label.slice(0, 10), 145, 300);
  });
}

/** 상세 보기에서 원본이 404 일 때. 카드 종횡비 그대로. */
export function fallbackDetailCard(rank: number, label: string): THREE.Texture {
  return canvasTex(1024, 1432, (ctx) => {
    roundedRect(ctx, 24, 24, 976, 1384, 48);
    ctx.fillStyle = RANK_COLOR[rank] ?? '#888';
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    ctx.lineWidth = 14;
    ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fillRect(90, 120, 844, 740);
    ctx.fillStyle = 'rgba(0,0,0,0.8)';
    ctx.font = 'bold 84px ui-monospace, monospace';
    ctx.textAlign = 'center';
    ctx.fillText(label.slice(0, 12), 512, 1040);
    ctx.font = '48px ui-monospace, monospace';
    ctx.fillText('(카드 그림 없음)', 512, 1140);
  });
}

/** 수량 배지 — 어두운 반투명 원 + 흰 xN. 값마다 한 번만 만든다. */
export function makeBadgeTexture(count: number): THREE.Texture {
  return canvasTex(128, 128, (ctx) => {
    ctx.beginPath();
    ctx.arc(64, 64, 58, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(12,14,20,0.82)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineWidth = 5;
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const t = `x${count}`;
    ctx.font = `bold ${t.length >= 4 ? 40 : t.length === 3 ? 50 : 60}px ui-monospace, monospace`;
    ctx.fillText(t, 64, 68);
  });
}

/**
 * 떠오르는 금액 텍스트(`-$4.49`)의 텍스처. **HTML 오버레이가 아니라 three 쿼드**로 간다:
 * 팩과 같은 공간(같은 카메라·원근)에 있어야 팩 위에 붙어 보이고, 오버레이로 하면 매 프레임
 * style 을 다시 써서 레이아웃을 건드려야 한다 (CSS animation 은 금지라 대안이 없다).
 * `makeBadgeTexture` 와 같은 캔버스→텍스처 방식이다.
 *
 * 폭은 글자 수에 따라 늘린다 — 고정 캔버스에 긴 글자를 넣으면 좌우가 눌린다.
 * 반환값의 `image.width/height` 로 렌더가 종횡비를 얻는다.
 */
export function makeMoneyTexture(text: string, color: string): THREE.Texture {
  const px = 96; // 캔버스 안 글자 크기 (world unit 크기는 econ.floatSize 가 정한다)
  const w = Math.max(128, Math.ceil((text.length * px * 0.66 + px) / 4) * 4);
  const h = px * 2;
  return canvasTex(w, h, (ctx) => {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `bold ${px}px ui-monospace, Menlo, Consolas, monospace`;
    // 검은 테두리 — 팩 그림이 밝든 어둡든 글자가 읽혀야 한다
    ctx.lineJoin = 'round';
    ctx.lineWidth = px * 0.22;
    ctx.strokeStyle = 'rgba(8,8,10,0.92)';
    ctx.strokeText(text, w / 2, h / 2);
    ctx.fillStyle = color;
    ctx.fillText(text, w / 2, h / 2);
  });
}

export function makeFallbackSet(perfFrac: number): TexSet {
  const fronts = {} as Record<CardKind, THREE.Texture>;
  for (const k of CARD_KINDS) fronts[k] = fallbackCard(k);
  return {
    packFront: fallbackPackFront(perfFrac),
    packInner: fallbackPackInner(),
    cardBack: fallbackCard('card_back'),
    cardFronts: fronts,
    binderCover: fallbackBinderCover(),
    binderSheet: fallbackBinderSheet(false),
    binderSheetBack: fallbackBinderSheet(true),
    binderGloss: fallbackBinderGloss(false),
    binderGlossBack: fallbackBinderGloss(true),
  };
}

export interface AssetMeta {
  perfFrac?: number;
  binder?: BinderMeta;
}

// ------------------------------------------------------- 카드 그림 (파일 하나 → 텍스처 둘)
// 아틀라스를 없앤 뒤(2026-09-10) 카드 그림은 **카드당 파일 하나**다. 칸에는 줄여서,
// 상세 보기에는 원본 크기로 올리는데 **같은 파일을 두 번 받지 않으려고** blob 을 한 번만 받고
// 그것을 두 번 디코딩한다 (같은 그림을 두 번 받던 것이 아틀라스 시절의 낭비였다).

/** 애셋 파일 하나를 blob 으로. 404 는 null (그 카드 그림이 아직 없는 것은 정상이다). */
export function fetchAssetBlob(url: string): Promise<Blob | null> {
  return fetch(url)
    .then((r) => (r.ok ? r.blob() : null))
    .catch(() => null);
}

function imageFromBlob(blob: Blob): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

/** blob → 원본 크기 텍스처 (상세 보기). 칸 텍스처(≈320px)를 확대하면 글자가 뭉개진다. */
export async function textureFromBlob(blob: Blob): Promise<THREE.Texture | null> {
  const img = await imageFromBlob(blob);
  return img ? prep(new THREE.Texture(img)) : null;
}

/**
 * blob → 폭 `w` 로 줄인 텍스처 (칸). `createImageBitmap` 의 resize 를 쓰면 **디코딩 단계에서**
 * 크기가 정해져 원본 픽셀이 메인 스레드에 올라오지 않는다. 옵션을 무시하거나 지원하지 않는
 * 브라우저에서는 캔버스가 다시 줄이므로 **결과 크기는 같다** (비용만 는다).
 * 캔버스로 한 번 복사하는 이유는 ImageBitmap 을 그대로 텍스처로 쓰면 three 가 `flipY` 를
 * 무시해 브라우저마다 상하가 갈리기 때문이다 — 캔버스 텍스처는 일반 이미지와 똑같이 동작한다.
 */
export async function cellTextureFromBlob(blob: Blob, w: number, h: number): Promise<THREE.Texture | null> {
  const cw = Math.max(1, Math.round(w));
  const ch = Math.max(1, Math.round(h));
  const c = document.createElement('canvas');
  c.width = cw;
  c.height = ch;
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  try {
    const bmp = await createImageBitmap(blob, { resizeWidth: cw, resizeHeight: ch, resizeQuality: 'high' });
    ctx.drawImage(bmp, 0, 0, cw, ch);
    bmp.close();
  } catch {
    const img = await imageFromBlob(blob);
    if (!img) return null;
    ctx.drawImage(img, 0, 0, cw, ch);
  }
  return prep(new THREE.CanvasTexture(c));
}

/**
 * 애셋 URL 뒤에 붙일 버전. 빌드할 때마다 값이 바뀐다.
 *
 * **이게 없으면 이미지를 바꿔도 브라우저가 옛 파일을 계속 쓴다.** `cards/<id>.webp` 처럼
 * 이름이 고정인 게임 아트는 내용이 바뀌어도 URL 이 그대로라 캐시가 안 깨진다.
 * 서버 헤더(`no-cache`)를 고쳐도 **이미 브라우저에 박힌 캐시**는 만료될 때까지 남는다.
 * URL 을 바꾸는 것이 유일하게 확실한 방법이다.
 */
const ASSET_V = import.meta.env.DEV ? 'dev' : __BUILD_ID__;

export function assetsBase(): string {
  return `${import.meta.env.BASE_URL}assets/`;
}

/** 캐시를 우회해야 하는 애셋 URL. 이름이 고정인 게임 아트에 쓴다. */
export function assetUrl(path: string): string {
  return `${assetsBase()}${path}${path.includes('?') ? '&' : '?'}v=${ASSET_V}`;
}

/** 실제 이미지를 비동기로 불러와 콜백으로 하나씩 교체. 실패한 항목은 폴백 유지. */
export function loadRealAssets(onTex: (name: string, tex: THREE.Texture) => void, onMeta: (m: AssetMeta) => void): void {
  const loader = new THREE.TextureLoader();
  const base = `${import.meta.env.BASE_URL}assets/`;
  // 개봉 화면 7장(팩 2 + 카드 뒷면 1 + 등급 폴백 4) + 바인더 5장. 카드별 앞면 그림은 여기서
  // 안 받는다 — 뽑기 결과에 따라 달라져서 CardTextureCache 가 팩 단위로 받고 버린다.
  const names: string[] = ['pack_front', 'pack_inner', 'card_back', 'card_common', 'card_rare', 'card_ar', 'card_ur', ...BINDER_TEX_NAMES];
  for (const n of names) {
    loader.load(
      `${base}${n}.webp`,
      (t) => onTex(n, prep(t)),
      undefined,
      () => console.info(`[assets] ${n}.webp 없음 → 폴백 유지`),
    );
  }
  fetch(`${base}assets.json?v=${ASSET_V}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((m) => {
      if (m && typeof m.perfFrac === 'number') onMeta({ perfFrac: m.perfFrac });
    })
    .catch(() => {});
  fetch(`${base}binder.json`)
    .then((r) => (r.ok ? r.json() : null))
    .then((m) => {
      if (m) onMeta({ binder: m as BinderMeta });
    })
    .catch(() => {});
}
